import React, { useEffect, useState } from "react";
import { Facebook, Instagram, Music2, MessageCircle, MapPin, MessageSquare, Mail } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api, buildWaLink } from "../lib/api";

const DEFAULT_LOGO = "/logo-hero.png";

// Nomor Malaysia (WhatsApp): +60 18 286 2358
const PHONE_MY_DISPLAY = "+60 18 286 2358";
const PHONE_MY_WA = "60182862358";

const XHS = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1zm2.4 5.6v4.8h1.2v-1.6h1.6c1 0 1.6-.6 1.6-1.6S11.2 9.6 10.2 9.6H7.4zm1.2 1v1.2h1.4c.3 0 .5-.2.5-.6s-.2-.6-.5-.6H8.6zm5.4-1l-1.6 4.8h1.2l.3-1h1.6l.3 1h1.2l-1.6-4.8H14zm.4 1l.6 1.8h-1.2l.6-1.8z"/>
  </svg>
);

const LineIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63h2.386c.345 0 .63.285.63.63 0 .349-.285.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .629.285.629.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
  </svg>
);

const SOCIALS = [
  { Icon: Facebook, label: "Facebook", href: "#" },
  { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/ceotravelbatam" },
  { Icon: XHS, label: "Xiaohongshu", href: "#" },
  { Icon: Music2, label: "TikTok", href: "https://www.tiktok.com/@ceotravelbatam" },
  { Icon: LineIcon, label: "LINE", href: "https://line.me/ti/p/nLGRbyBSqH" },
];

export const Footer = ({ onContact }) => {
  const { t } = useLang();
  const [branding, setBranding] = useState({
    logo_url: DEFAULT_LOGO,
    email: "ceotravel88@gmail.com",
    phone: "+62 813 9997 828",
    address: "Ruko Lotus Garden Blok AA No. 10\nBatam Center, Kepulauan Riau",
  });

  useEffect(() => {
    let mounted = true;
    api
      .get("/settings/branding")
      .then((r) => {
        if (!mounted || !r.data) return;
        setBranding((prev) => ({
          logo_url: r.data.logo_url || prev.logo_url,
          email: r.data.email || prev.email,
          phone: r.data.phone || prev.phone,
          address: r.data.address || prev.address,
        }));
      })
      .catch((err) => console.warn("branding fetch failed", err));
    return () => {
      mounted = false;
    };
  }, []);

  const waMy = `https://wa.me/${PHONE_MY_WA}?text=${encodeURIComponent(t.waMessages.consult)}`;

  return (
    <footer
      id="kontak"
      data-testid="footer"
      className="bg-[#0C2230] text-[#FBFAF6]"
    >
      <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <a href="#top" className="inline-flex items-center">
              <span className="inline-flex items-center">
                <img
                  src={branding.logo_url}
                  alt="CEO Travel"
                  className="h-20 w-auto object-contain drop-shadow-[0_3px_10px_rgba(0,0,0,0.5)] md:h-24"
                />
              </span>
            </a>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/65">
              {t.footer.about}
            </p>
            <button
              onClick={onContact}
              data-testid="footer-contact-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#B7892F]/60 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#B7892F] transition-colors hover:bg-[#B7892F] hover:text-white"
            >
              {t.nav.cta}
            </button>
          </div>

          <div className="md:col-span-3">
            <p className="text-[11px] uppercase tracking-[0.28em] text-white/45">
              {t.footer.contact}
            </p>
            <ul className="mt-5 space-y-3 text-sm text-white/80">
              {/* WhatsApp Indonesia */}
              <li className="flex items-start gap-2.5">
                <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#0E9A57]" />
                <a
                  data-testid="footer-whatsapp"
                  href={buildWaLink(t.waMessages.consult)}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white"
                >
                  <span className="mr-1.5">🇮🇩</span>
                  {branding.phone}
                </a>
              </li>

              {/* WhatsApp Malaysia */}
              <li className="flex items-start gap-2.5">
                <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#0E9A57]" />
                <a
                  data-testid="footer-whatsapp-my"
                  href={waMy}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white"
                >
                  <span className="mr-1.5">🇲🇾</span>
                  {PHONE_MY_DISPLAY}
                </a>
              </li>

              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#1E7FC2]" />
                <a
                  data-testid="footer-email"
                  href={`mailto:${branding.email}`}
                  className="hover:text-white"
                >
                  {branding.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#B7892F]" />
                <span className="whitespace-pre-line">{branding.address}</span>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.28em] text-white/45">
              {t.footer.follow}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {SOCIALS.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target={href !== "#" ? "_blank" : undefined}
                  rel={href !== "#" ? "noreferrer" : undefined}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-all hover:border-[#B7892F] hover:text-[#B7892F]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/45 sm:flex-row sm:items-center">
          <span>
            © {new Date().getFullYear()} CEO Travel. {t.footer.rights}
          </span>
          <span className="font-[Fraunces] italic text-white/55">
            Travel More · Discover More · Live More
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
